import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatBRL, formatDateTime, whatsappHref, apiErrorMsg, TIPOS_ORGAO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Building2, Phone, MessageCircle, Trophy, TrendingDown, ArrowRight,
  Edit, Ban, Mail, Globe, MapPin, Users, Star, Send, Target, FileText, Plus, Trash2, Calendar, Search, X, Sparkles, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import StarRating from "@/components/StarRating";
import ContatoRegistrarModal from "@/components/ContatoRegistrarModal";
import PessoaPerfilModal from "@/components/PessoaPerfilModal";
import { InscricaoModal } from "@/pages/FunilBoard";
import ConfirmacaoEmailModal from "@/components/ConfirmacaoEmailModal";
import RegistrarEnvioManualModal from "@/components/RegistrarEnvioManualModal";
import IndicarCelModal from "@/components/IndicarCelModal";

const ESTAGIO_LABELS = {
  nao_contatado: "Carteira", incerto: "Incertos", interessado: "Interessados",
  certo: "Certos", inscrito: "Inscritos", nao_querem: "Negativas",
};

const ESTAGIO_CHIP = {
  nao_contatado: "bg-slate-100 text-slate-600",
  incerto: "bg-slate-900 text-white",
  interessado: "bg-blue-100 text-blue-700",
  certo: "bg-indigo-100 text-indigo-700",
  inscrito: "bg-emerald-100 text-emerald-700",
  nao_querem: "bg-red-100 text-red-700",
};

const CAT_LABEL = { cliente: "Cliente", consumidor: "Consumidor", nao_cliente: "Não Cliente" };
const CAT_CLASS = {
  cliente: "bg-emerald-500 text-white",
  consumidor: "bg-amber-500 text-white",
  nao_cliente: "bg-red-500 text-white",
};

function CategoriaBadge({ cat, freq }) {
  if (!cat) return null;
  return (
    <span
      className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${CAT_CLASS[cat] || "bg-slate-300 text-slate-800"}`}
      title={`${CAT_LABEL[cat]} · ${freq ?? 0} inscriç${(freq ?? 0) === 1 ? "ão" : "ões"}`}
      data-testid={`badge-cat-${cat}`}
    >
      {CAT_LABEL[cat]}
    </span>
  );
}

// Iter 40: Input de e-mails múltiplos em formato chips
function EmailsChipsInput({ emails, onChange }) {
  const [input, setInput] = useState("");
  const isValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const add = () => {
    const v = input.trim().replace(/,$/, "");
    if (!v) return;
    if (!isValid(v)) {
      toast.error(`E-mail inválido: ${v}`);
      return;
    }
    if ((emails || []).includes(v)) {
      toast.error("E-mail já adicionado");
      setInput("");
      return;
    }
    onChange([...(emails || []), v]);
    setInput("");
  };
  const remove = (e) => onChange((emails || []).filter((x) => x !== e));
  return (
    <div
      className="min-h-[42px] flex flex-wrap items-center gap-1.5 p-2 border border-slate-300 rounded-md bg-white"
      data-testid="emails-chips-container"
    >
      {(emails || []).map((e) => (
        <span
          key={e}
          className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full pl-2 pr-1 py-0.5 text-xs"
          data-testid={`email-chip-${e}`}
        >
          <Mail className="w-3 h-3" />
          {e}
          <button
            type="button"
            onClick={() => remove(e)}
            className="ml-1 w-4 h-4 rounded-full bg-white hover:bg-red-100 hover:text-red-600 text-slate-500 flex items-center justify-center text-[10px]"
            aria-label={`Remover ${e}`}
            data-testid={`btn-remover-email-${e}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
      <input
        type="email"
        value={input}
        onChange={(ev) => setInput(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === ",") {
            ev.preventDefault();
            add();
          } else if (ev.key === "Backspace" && !input && (emails || []).length > 0) {
            onChange((emails || []).slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={(emails || []).length === 0 ? "email@exemplo.gov.br" : ""}
        className="flex-1 min-w-[160px] border-0 outline-0 text-sm bg-transparent"
        data-testid="email-chip-input"
      />
    </div>
  );
}


function BlacklistPessoaBtn({ pessoa, onChanged }) {
  const { user } = useAuth();
  const [dlg, setDlg] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const isBL = !!pessoa.blacklist;
  const podeRemover = user?.role === "diretoria";

  const abrir = () => {
    if (isBL && !podeRemover) {
      toast.error("Apenas Diretoria pode remover Blacklist manualmente.");
      return;
    }
    setMotivo(pessoa.motivo_blacklist || "");
    setDlg(true);
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      if (isBL) {
        await api.patch(`/pessoas/${pessoa.id}/blacklist`, { blacklist: false, motivo: "" });
        toast.success("Removido da Blacklist");
      } else {
        if (!motivo.trim()) { toast.error("Descreva o motivo"); setBusy(false); return; }
        await api.patch(`/pessoas/${pessoa.id}/blacklist`, { blacklist: true, motivo: motivo.trim() });
        toast.success("Marcado como Blacklist");
      }
      setDlg(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={abrir}
        className={`min-w-[110px] border-2 font-black uppercase tracking-widest text-[11px] ${isBL ? "bg-red-600 border-red-700 text-white hover:bg-red-700" : "border-red-400 text-red-600 hover:bg-red-50"}`}
        data-testid={`btn-blacklist-${pessoa.id}`}
        title={isBL ? (pessoa.motivo_blacklist || "Cliente na concorrência") : "Marcar como Blacklist"}
      >
        <Ban className="w-3.5 h-3.5 mr-1" /> Blacklist
      </Button>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="dialog-blacklist-pessoa">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🚫</span>
              {isBL ? "Remover da Blacklist" : "Marcar como Blacklist"}
            </DialogTitle>
            <DialogDescription>
              Marque uma pessoa como cliente na concorrência. Ela sai automaticamente ao efetivar uma inscrição.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600"><b>{pessoa.nome}</b> — {pessoa.funcao}</p>
            {isBL ? (
              <p className="text-sm text-slate-600">Motivo atual: <i>{pessoa.motivo_blacklist || "—"}</i>.</p>
            ) : (
              <>
                <Label className="text-xs uppercase text-slate-500 tracking-widest">Por que Blacklist? *</Label>
                <Textarea
                  rows={3}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: cliente atual da concorrência X, contrato exclusivo, indisponibilidade orçamentária..."
                  data-testid="input-motivo-blacklist"
                />
                <p className="text-[11px] text-slate-500">
                  A pessoa sai da Blacklist automaticamente ao efetivar uma inscrição em curso.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} disabled={busy}>Cancelar</Button>
            <Button
              onClick={confirmar}
              disabled={busy}
              className={isBL ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="btn-confirmar-blacklist-pessoa"
            >
              {busy ? "Salvando..." : (isBL ? "Confirmar remoção" : "Marcar Blacklist")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MunicipioPerfil() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const catFiltro = searchParams.get("cat");
  const [mun, setMun] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [stats12m, setStats12m] = useState(null);
  const [medals, setMedals] = useState(null);
  const [cardsAtivos, setCardsAtivos] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [blOpen, setBlOpen] = useState(false);
  const [blMotivo, setBlMotivo] = useState("");
  const [contatoModal, setContatoModal] = useState(null); // { pessoa, canal }
  const [enviarModal, setEnviarModal] = useState(null); // { pessoa }
  const [inscricaoCard, setInscricaoCard] = useState(null); // card selecionado para registrar inscrição
  const [enviarConfInscricaoId, setEnviarConfInscricaoId] = useState(null); // id da inscrição p/ enviar confirmação
  const [envioManualIns, setEnvioManualIns] = useState(null); // Iter 62: {id, destinatariosSugeridos}
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  const [timelinePessoaId, setTimelinePessoaId] = useState(""); // "" = todas
  const [pessoaPerfilId, setPessoaPerfilId] = useState(null); // abrir modal perfil pessoa
  const [buscaPessoa, setBuscaPessoa] = useState(""); // Iter 40: busca por nome de pessoa
  const [novaPessoaOpen, setNovaPessoaOpen] = useState(false);
  const [lixeiraOpen, setLixeiraOpen] = useState(false);
  const [pessoasDeletadas, setPessoasDeletadas] = useState([]);
  const [lixeiraBusy, setLixeiraBusy] = useState(false);
  const [lixeiraLoading, setLixeiraLoading] = useState(false);
  const [indicarCelOpen, setIndicarCelOpen] = useState(false);

  // Carrega lixeira quando modal abre (só diretoria terá permissão)
  useEffect(() => {
    if (lixeiraOpen && id) {
      setLixeiraLoading(true);
      api.get(`/pessoas-lixeira?municipio_id=${id}`)
        .then((r) => setPessoasDeletadas(r.data || []))
        .catch(() => setPessoasDeletadas([]))
        .finally(() => setLixeiraLoading(false));
    }
  }, [lixeiraOpen, id]);
  const [novaPessoa, setNovaPessoa] = useState({ nome: "", nome_urna: "", funcao: "Vereador", telefone: "", whatsapp: "", email: "", data_nascimento: "", instagram: "", partido: "" });

  const criarPessoa = async () => {
    if (!novaPessoa.nome.trim()) { toast.error("Informe o nome"); return; }
    try {
      const payload = { ...novaPessoa, municipio_id: id };
      // remover campos vazios pra não gravar strings vazias
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/pessoas", payload);
      toast.success("Pessoa cadastrada");
      setNovaPessoaOpen(false);
      setNovaPessoa({ nome: "", nome_urna: "", funcao: "Vereador", telefone: "", whatsapp: "", email: "", data_nascimento: "", instagram: "", partido: "" });
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro ao criar pessoa")); }
  };

  const load = async () => {
    const [m, t, p, s, md, ca] = await Promise.all([
      api.get(`/municipios/${id}`),
      api.get(`/municipios/${id}/timeline`),
      api.get(`/pessoas?municipio_id=${id}`),
      api.get(`/municipios/${id}/stats-12m`).catch(() => ({ data: null })),
      api.get(`/medals`).catch(() => ({ data: null })),
      api.get(`/municipios/${id}/cards-ativos`).catch(() => ({ data: [] })),
    ]);
    setMun(m.data); setTimeline(t.data); setPessoas(p.data);
    setInscricoes(t.data.filter((x) => x.tipo === "inscricao"));
    setStats12m(s.data);
    setMedals(md.data);
    setCardsAtivos(ca.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const salvarEdit = async () => {
    try {
      await api.patch(`/municipios/${id}`, form);
      toast.success("Município atualizado");
      setEditOpen(false); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const toggleBlacklist = async () => {
    try {
      await api.patch(`/municipios/${id}/blacklist`, {
        blacklist: !mun.blacklist,
        motivo: mun.blacklist ? "" : blMotivo,
      });
      toast.success(mun.blacklist ? "Removido da blacklist" : "Adicionado à blacklist");
      setBlOpen(false); setBlMotivo(""); load();
    } catch { toast.error("Erro"); }
  };

  const setEstrelas = async (pessoa, estrelas) => {
    try {
      await api.patch(`/pessoas/${pessoa.id}/estrelas`, { estrelas });
      setPessoas((prev) => prev.map((p) => p.id === pessoa.id ? { ...p, estrelas } : p));
    } catch { toast.error("Erro ao salvar receptividade"); }
  };

  const togglePontoFocal = async (pessoa) => {
    const novo = !pessoa.ponto_focal;
    try {
      await api.patch(`/pessoas/${pessoa.id}/ponto-focal`, { ponto_focal: novo });
      setPessoas((prev) => prev.map((p) => {
        if (p.id === pessoa.id) return { ...p, ponto_focal: novo };
        // Remove ponto_focal dos outros se marcou
        if (novo && p.ponto_focal) return { ...p, ponto_focal: false };
        return p;
      }));
      toast.success(novo ? `${pessoa.nome} agora é o Ponto Focal` : "Ponto Focal removido");
    } catch { toast.error("Erro"); }
  };

  const ultimoContatoPorPessoa = useMemo(() => {
    const m = {};
    for (const e of timeline) {
      if (e.tipo === "contato" && e.payload?.pessoa_id) {
        const pid = e.payload.pessoa_id;
        if (!m[pid] || e.data > m[pid]) m[pid] = e.data;
      }
    }
    return m;
  }, [timeline]);

  const salvarInscricao = async (payload) => {
    try {
      await api.post("/inscricoes", payload);
      toast.success("🎉 Inscrição fechada!");
      setInscricaoCard(null);
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro")); }
  };

  // Iter 35: cancelar inscrição
  const cancelarInscricao = async (iid, quantidade) => {
    const motivo = window.prompt(
      `Cancelar inscrição de ${quantidade} pessoa${quantidade > 1 ? "s" : ""}?\n\nDigite o motivo (opcional):`
    );
    if (motivo === null) return; // usuário clicou Cancelar
    try {
      const r = await api.delete(`/inscricoes/${iid}${motivo ? `?motivo=${encodeURIComponent(motivo)}` : ""}`);
      const nfs = r.data?.nfs_a_cancelar || [];
      toast.success("🗑️ Inscrição cancelada", nfs.length ? { description: `O Fiscal foi avisado para cancelar a NF nº ${nfs.join(", ")}.`, duration: 8000 } : undefined);
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao cancelar"));
    }
  };

  // Iter 35: remover 1 aluno de uma inscrição (reduz qtd e retira ID)
  const removerAlunoInscricao = async (ins, alunoId) => {
    const p = ins.payload || {};
    const alunos = (p.alunos_pessoa_ids || []).filter((x) => x !== alunoId);
    const novaQtd = Math.max(1, (p.quantidade || 1) - 1);
    if (!window.confirm(`Remover este aluno da inscrição? A quantidade passará para ${novaQtd}.`)) return;
    try {
      // Valor rateado proporcionalmente
      const totalAtual = Number(p.valor_vendido || 0);
      const cheioAtual = Number(p.valor_cheio || totalAtual);
      const qtdAtual = Number(p.quantidade || 1);
      const proporcao = novaQtd / qtdAtual;
      await api.patch(`/inscricoes/${p.inscricao_id || ins.ref_id}`, {
        quantidade: novaQtd,
        valor_vendido: Number((totalAtual * proporcao).toFixed(2)),
        valor_cheio: Number((cheioAtual * proporcao).toFixed(2)),
        alunos_pessoa_ids: alunos,
      });
      toast.success("Aluno removido da inscrição");
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro"));
    }
  };

  if (!mun) return <div className="text-slate-500">Carregando...</div>;
  const investido = inscricoes.reduce((s, e) => s + e.payload.valor_vendido, 0);

  // Ordena pessoas: ponto focal primeiro, depois **último contato mais recente** (item 6),
  // depois estrelas desc, depois nome.

  // Ordena: 1º por categoria (cliente → consumidor → não cliente); dentro de cada grupo, ponto focal primeiro,
  // depois último contato mais recente, depois estrelas desc, depois nome asc. Institucionais e blacklist vão ao final.
  const _catRank = (p) => {
    if (p.blacklist) return 90;
    if (p.institucional) return 80;
    const c = p.categoria || (p.frequencia >= 2 ? "cliente" : p.frequencia === 1 ? "consumidor" : "nao_cliente");
    if (c === "cliente") return 0;
    if (c === "consumidor") return 1;
    if (c === "nao_cliente") return 2;
    return 50;
  };

  const pessoasOrdenadas = [...pessoas]
    .filter((p) => {
      if (!catFiltro) return true;
      if (catFiltro === "blacklist") return !!p.blacklist;
      const c = p.categoria || (p.frequencia >= 2 ? "cliente" : p.frequencia === 1 ? "consumidor" : "nao_cliente");
      return c === catFiltro;
    })
    // Iter 40: busca por nome, nome_urna ou função
    .filter((p) => {
      const q = buscaPessoa.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.nome || "").toLowerCase().includes(q) ||
        (p.nome_urna || "").toLowerCase().includes(q) ||
        (p.funcao || "").toLowerCase().includes(q) ||
        (p.partido || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const ra = _catRank(a);
      const rb = _catRank(b);
      if (ra !== rb) return ra - rb;
      if (a.ponto_focal && !b.ponto_focal) return -1;
      if (!a.ponto_focal && b.ponto_focal) return 1;
      const ca = ultimoContatoPorPessoa[a.id] || "";
      const cb = ultimoContatoPorPessoa[b.id] || "";
      if (ca !== cb) return cb.localeCompare(ca); // mais recente primeiro
      if ((b.estrelas || 0) !== (a.estrelas || 0)) return (b.estrelas || 0) - (a.estrelas || 0);
      return (a.nome || "").localeCompare(b.nome || "");
    });

  const isVendedora = user?.role === "vendedora";

  return (
    <div className="space-y-6" data-testid="municipio-perfil">
      {/* Banner Diretoria/Supervisão — nome da vendedora responsável */}
      {!isVendedora && (
        <div
          className="rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-3 flex items-center gap-3 flex-wrap"
          data-testid="banner-vendedora-responsavel"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">
            {mun.vendedora_nome ? mun.vendedora_nome.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") : "—"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase font-bold tracking-widest text-amber-700">
              Vendedora responsável
            </div>
            <div className="font-display text-lg font-bold text-slate-900 truncate flex items-center gap-2 flex-wrap">
              {mun.vendedora_codigo && (
                <span className="text-[11px] font-black bg-amber-500 text-white rounded px-1.5 py-0.5">
                  {mun.vendedora_codigo}
                </span>
              )}
              {mun.vendedora_nome || <span className="italic text-slate-500 font-normal">Sem vendedora atribuída</span>}
            </div>
          </div>
          <div className="text-[11px] uppercase font-bold tracking-widest text-amber-700 bg-white/60 rounded px-2 py-1">
            {user?.role === "diretoria" ? "Visão da Diretoria" : "Visão da Supervisão"}
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${mun.blacklist ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
            {mun.blacklist ? <Ban className="w-8 h-8" /> : <Building2 className="w-8 h-8" />}
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
              {mun.nome}
              {medals && (medals.municipios?.[id] || 0) >= 5 && <span title="+5 cursos no ano" className="text-2xl">🥇</span>}
              {medals?.top_municipios_ids?.includes(id) && <span title="Top 10 do ano" className="text-2xl">🏆</span>}
            </h1>
            <div className="text-slate-500 text-sm">
              {mun.uf} · {mun.tipo_orgao} · {medals?.municipios?.[id] || 0} inscrição(ões) no ano
            </div>
            {mun.blacklist && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded">
                <Ban className="w-3 h-3" /> Blacklist: {mun.motivo_blacklist || "sem motivo"}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <AcaoCurso
            cardsAtivos={cardsAtivos}
            variant="inscricao"
            onPick={(card) => setInscricaoCard(card)}
          />
          <AcaoCurso
            cardsAtivos={cardsAtivos}
            variant="proposta"
            onPick={(card) => window.open(`/proposta/${card.id}`, "_blank")}
          />
          <Button variant="outline" onClick={() => { setForm({ ...mun }); setEditOpen(true); }} data-testid="btn-editar-mun">
            <Edit className="w-4 h-4 mr-1" /> Editar
          </Button>
          <Button
            variant="outline"
            onClick={() => { if (mun.blacklist) { toggleBlacklist(); } else { setBlOpen(true); } }}
            className={mun.blacklist ? "text-emerald-700 border-emerald-200" : "text-red-700 border-red-200"}
            data-testid="btn-blacklist"
          >
            <Ban className="w-4 h-4 mr-1" /> {mun.blacklist ? "Recuperar" : "Blacklist"}
          </Button>
        </div>
      </div>

      {/* Últimos 12 meses — destaque no topo */}
      <Card className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200" data-testid="card-stats-12m">
        <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 mb-3">
          🔥 Últimos 12 meses
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase">Inscrições</div>
            <div className="font-display text-3xl font-bold tabular-nums text-emerald-700">
              {stats12m?.qtd_inscricoes ?? 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase">Alunos</div>
            <div className="font-display text-3xl font-bold tabular-nums text-slate-900">
              {stats12m?.total_inscritos ?? 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase">Valor investido</div>
            <div className="font-display text-2xl font-bold tabular-nums text-blue-700">
              {formatBRL(stats12m?.valor_total || 0)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase">Ticket médio</div>
            <div className="font-display text-2xl font-bold tabular-nums text-slate-700">
              {formatBRL(stats12m?.ticket_medio || 0)}
            </div>
          </div>
        </div>
        {/* Histórico importado da carteira (informativo, não influencia cálculo dinâmico) */}
        {(mun?.frequencia_12m > 0 || (mun?.categoria && mun.categoria !== "nao_cliente")) && (
          <div className="mt-4 pt-4 border-t border-emerald-200/70" data-testid="historico-importado">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
              📥 Histórico importado da carteira
              <span className="text-slate-400 font-normal normal-case tracking-normal italic ml-1">
                (referência • dados atuais são calculados pelas pessoas)
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {typeof mun?.frequencia_12m === "number" && mun.frequencia_12m > 0 && (
                <span
                  className="bg-white/70 text-slate-700 rounded-md px-2 py-1 font-medium border border-slate-200"
                  data-testid="historico-frequencia"
                >
                  Frequência histórica: <b className="tabular-nums text-emerald-700">{mun.frequencia_12m}</b>
                  <span className="text-slate-400"> {mun.frequencia_12m === 1 ? "inscrição" : "inscrições"}</span>
                </span>
              )}
              {mun?.categoria && mun.categoria !== "nao_cliente" && (
                <span
                  className={
                    "rounded-md px-2 py-1 font-semibold text-xs uppercase tracking-widest " +
                    (mun.categoria === "cliente" ? "bg-emerald-500 text-white" :
                     mun.categoria === "deixou_de_vir" ? "bg-amber-500 text-white" :
                     mun.categoria === "blacklist" ? "bg-red-600 text-white" :
                     "bg-slate-300 text-slate-800")
                  }
                  data-testid="historico-categoria"
                >
                  {mun.categoria === "cliente" ? "Cliente" :
                   mun.categoria === "deixou_de_vir" ? "Deixou de Vir" :
                   mun.categoria === "blacklist" ? "Blacklist" :
                   mun.categoria === "cliente_potencial" ? "Cliente Potencial" :
                   mun.categoria}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Inscrições ativas — REMOVIDO: substituído por botões diretos no header */}


      {/* Inscrições realizadas — Iter 35 */}
      {inscricoes.length > 0 && (
        <Card className="p-6 rounded-2xl" data-testid="inscricoes-realizadas-section">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Inscrições realizadas
            <span className="text-sm font-normal text-slate-500">({inscricoes.length})</span>
          </h2>
          <div className="space-y-3">
            {inscricoes.map((e) => {
              const p = e.payload || {};
              const iid = p.inscricao_id || e.ref_id;
              const alunosNomes = p.alunos_nomes || [];
              return (
                <div
                  key={iid}
                  className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 hover:bg-white hover:border-slate-300 transition-colors"
                  data-testid={`inscricao-item-${iid}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">
                        {p.curso_nome || `Curso #${(p.curso_id || "").slice(0, 8)}`}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDateTime(e.data)}
                        </span>
                        <span>
                          <b>{p.quantidade}</b> pessoa{p.quantidade > 1 ? "s" : ""}
                        </span>
                        {p.cortesias > 0 && (
                          <span className="text-sky-700" data-testid={`mun-insc-cortesia-${p.inscricao_id}`}>
                            {p.cortesias} cortesia{p.cortesias > 1 ? "s" : ""}{p.cortesia_motivo ? ` · ${p.cortesia_motivo}` : ""}
                          </span>
                        )}
                        <span className="font-bold text-emerald-700">{formatBRL(p.valor_vendido)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[#1d7495] border-[#1d7495] hover:bg-[#e6f1f5]"
                        onClick={() => setEnviarConfInscricaoId(iid)}
                        data-testid={`btn-enviar-conf-fixo-${iid}`}
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" /> Confirmação
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-slate-700 border-slate-300 hover:bg-slate-100"
                        onClick={async () => {
                          try {
                            // Iter 64 · baixa PDF on-demand (regera sempre)
                            const res = await api.get(`/inscricoes/${iid}/baixar-pdf-confirmacao`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `confirmacao-inscricao-${iid.slice(0, 8)}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success("📄 PDF baixado");
                          } catch (e) {
                            toast.error(apiErrorMsg(e, "Erro ao baixar PDF"));
                          }
                        }}
                        data-testid={`btn-baixar-conf-${iid}`}
                        title="Baixar PDF da confirmação (regera com dados atuais)"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Baixar PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => setEnvioManualIns({ id: iid, destinatariosSugeridos: p.email_destinatarios_sugeridos || [] })}
                        data-testid={`btn-registrar-envio-manual-${iid}`}
                        title="Marcar que o e-mail foi enviado manualmente pelo seu Gmail/Outlook"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Já enviei
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => cancelarInscricao(iid, p.quantidade)}
                        data-testid={`btn-cancelar-inscricao-${iid}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                  {alunosNomes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                        Inscritos ({alunosNomes.length})
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {alunosNomes.map((a) => (
                          <span
                            key={a.id}
                            className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                            data-testid={`inscricao-aluno-${iid}-${a.id}`}
                          >
                            <span className="truncate max-w-[180px]">{a.nome || "—"}</span>
                            {alunosNomes.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removerAlunoInscricao(e, a.id)}
                                className="ml-1 w-4 h-4 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-500 flex items-center justify-center text-[10px]"
                                title="Remover este aluno da inscrição"
                                data-testid={`btn-remover-aluno-${iid}-${a.id}`}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Dados institucionais */}
      <Card className="p-6 rounded-2xl">
        <h2 className="font-display font-bold text-xl mb-4">Dados institucionais</h2>
        <div className="grid md:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <div><span className="text-slate-500">CNPJ:</span> <b>{mun.cnpj || "—"}</b></div>
          <div><span className="text-slate-500">Telefone:</span>{" "}
            {mun.telefone ? (
              <a
                href={whatsappHref(mun.telefone)}
                target="_blank"
                rel="noreferrer"
                className="font-bold text-emerald-700 hover:underline inline-flex items-center gap-1"
                data-testid="mun-telefone-wa"
                title="Abrir no WhatsApp Web"
              >
                <MessageCircle className="w-3 h-3" /> {mun.telefone}
              </a>
            ) : <b>—</b>}
          </div>
          <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /><span className="text-slate-500">E-mail:</span> <b>{mun.email_institucional || "—"}</b></div>
          {(mun.emails || []).length > 0 && (
            <div className="md:col-span-2 flex items-start gap-1 flex-wrap">
              <Mail className="w-3 h-3 text-slate-400 mt-1" />
              <span className="text-slate-500">E-mails adicionais:</span>
              <div className="flex flex-wrap gap-1">
                {(mun.emails || []).map((e) => (
                  <span key={e} className="text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-2 py-0.5" data-testid={`mun-email-${e}`}>{e}</span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-slate-400" /><span className="text-slate-500">Site:</span> <b>{mun.site ? <a className="text-blue-600 hover:underline" href={mun.site.startsWith("http") ? mun.site : `https://${mun.site}`} target="_blank" rel="noreferrer">{mun.site}</a> : "—"}</b></div>
          <div className="flex items-center gap-1"><Users className="w-3 h-3 text-slate-400" /><span className="text-slate-500">Vereadores:</span> <b>{mun.qtd_vereadores || "—"}</b></div>
          <div><span className="text-slate-500">Perfil político:</span> <b>{mun.perfil_politico || "—"}</b></div>
          <div className="md:col-span-2 flex items-start gap-1"><MapPin className="w-3 h-3 text-slate-400 mt-1" /><span className="text-slate-500">Endereço:</span> <b>{mun.endereco || "—"}</b></div>
          {mun.observacoes && <div className="md:col-span-2"><span className="text-slate-500">Observações:</span> <span className="italic">{mun.observacoes}</span></div>}
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total investido (histórico)</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-1">{formatBRL(investido)}</div>
        </Card>
        <Card className="p-5 rounded-2xl">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Cursos comprados</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-1">{inscricoes.length}</div>
        </Card>
        <Card className="p-5 rounded-2xl">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Pessoas cadastradas</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-1">{pessoas.length}</div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl">
              Pessoas vinculadas
              {catFiltro && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  · filtrando por <b>{CAT_LABEL[catFiltro] || catFiltro}</b> ({pessoasOrdenadas.length})
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setNovaPessoaOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="btn-nova-pessoa"
              >
                <Plus className="w-4 h-4 mr-1" /> Nova pessoa
              </Button>
              {user?.role === "diretoria" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLixeiraOpen(true)}
                  className="border-slate-300 text-slate-600 hover:bg-slate-100"
                  title="Ver pessoas excluídas neste município"
                  data-testid="btn-lixeira-pessoas"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Lixeira
                </Button>
              )}
              {user && !(user.role === "gestao_cel" || user.role === "indicador" || (user.role === "vendedora" && user.nucleo_nome === "CeL Consultoria")) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIndicarCelOpen(true)}
                  className="border-sky-300 text-sky-700 hover:bg-sky-50"
                  title="Indicar este município para o time CeL Consultoria"
                  data-testid="btn-indicar-cel"
                >
                  <Sparkles className="w-4 h-4 mr-1" /> Indicar para CeL
                </Button>
              )}
              <span className="text-xs text-slate-400">Toque na estrela para marcar Ponto Focal</span>
            </div>
          </div>
          {/* Iter 40: busca dentro do município */}
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={buscaPessoa}
              onChange={(e) => setBuscaPessoa(e.target.value)}
              placeholder="Buscar pessoa por nome, nome de urna, função ou partido..."
              className="pl-9"
              data-testid="input-busca-pessoa-municipio"
            />
            {buscaPessoa && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                {pessoasOrdenadas.length} resultado(s)
              </span>
            )}
          </div>
          {catFiltro && (
            <div className="mb-3 flex items-center justify-between gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="text-sm text-blue-800">
                Vista filtrada · categoria <b>{CAT_LABEL[catFiltro] || catFiltro}</b> ({pessoasOrdenadas.length})
              </div>
              <Button
                onClick={() => nav(`/municipio/${id}`)}
                variant="outline"
                size="sm"
                className="border-blue-300 text-blue-700 hover:bg-blue-100 font-bold whitespace-nowrap"
                data-testid="btn-limpar-filtro-cat"
              >
                Ver todas as pessoas
              </Button>
            </div>
          )}
          <div className="space-y-3">
            {pessoasOrdenadas.map((p) => {
              const cnt = medals?.pessoas?.[p.id] || 0;
              const medal = cnt >= 5 ? "🥇" : cnt >= 3 ? "🥈" : cnt >= 2 ? "🥉" : null;
              return (
                <div
                  key={p.id}
                  className={`rounded-xl p-3 border-2 ${p.ponto_focal ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
                  data-testid={`pessoa-row-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => togglePontoFocal(p)}
                          className={`transition ${p.ponto_focal ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                          title={p.ponto_focal ? "Remover Ponto Focal" : "Marcar como Ponto Focal"}
                          data-testid={`btn-pf-${p.id}`}
                        >
                          <Star className={`w-4 h-4 ${p.ponto_focal ? "fill-amber-400" : ""}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPessoaPerfilId(p.id)}
                          className="truncate text-left hover:text-blue-700 hover:underline underline-offset-2 font-semibold"
                          data-testid={`btn-abrir-pessoa-${p.id}`}
                          title="Ver perfil e histórico da pessoa"
                        >
                          {p.nome}
                        </button>
                        {p.partido && <span className="text-xs text-slate-400 font-normal">({p.partido})</span>}
                        {p.institucional ? (
                          <span
                            className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-300 text-slate-800"
                            title="Contato institucional — não participa da categorização comercial"
                            data-testid={`badge-institucional-${p.id}`}
                          >Institucional</span>
                        ) : (
                          <CategoriaBadge cat={p.categoria || (p.frequencia >= 2 ? "cliente" : p.frequencia === 1 ? "consumidor" : "nao_cliente")} freq={p.frequencia} />
                        )}
                        {ultimoContatoPorPessoa[p.id] && (
                          <span
                            className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700"
                            title={`Último contato em ${new Date(ultimoContatoPorPessoa[p.id]).toLocaleString("pt-BR")}`}
                            data-testid={`ultimo-contato-${p.id}`}
                          >
                            📞 {new Date(ultimoContatoPorPessoa[p.id]).toLocaleDateString("pt-BR")}
                          </span>
                        )}
                        {medal && <span title={`${cnt} inscrições no ano`}>{medal}</span>}
                        {p.ponto_focal && (
                          <span className="text-[10px] uppercase font-bold tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded">Ponto Focal</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-1.5">
                        <span>{p.funcao}</span>
                        {p.telefone && (
                          <>
                            <span>·</span>
                            <a
                              href={whatsappHref(p.telefone)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-700 hover:underline font-medium inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`pessoa-telefone-wa-${p.id}`}
                              title="Abrir no WhatsApp Web"
                            >
                              <MessageCircle className="w-3 h-3" /> {p.telefone}
                            </a>
                          </>
                        )}
                        {p.whatsapp && p.whatsapp !== p.telefone && (
                          <>
                            <span>·</span>
                            <a
                              href={whatsappHref(p.whatsapp)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-700 hover:underline font-medium inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`pessoa-whatsapp-wa-${p.id}`}
                              title="Abrir no WhatsApp Web"
                            >
                              <MessageCircle className="w-3 h-3" /> WA: {p.whatsapp}
                            </a>
                          </>
                        )}
                      </div>
                      {p.email && <div className="text-xs text-slate-500">{p.email}</div>}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-slate-500 uppercase font-bold">Receptividade:</span>
                        <StarRating value={p.estrelas || 0} onChange={(v) => setEstrelas(p, v)} testid={`stars-${p.id}`} />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setContatoModal({ pessoa: p, canal: "ligacao" })}
                      className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50 min-w-[120px]"
                      data-testid={`btn-lig-${p.id}`}
                    >
                      <Phone className="w-4 h-4 mr-1" /> Ligação
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setContatoModal({ pessoa: p, canal: "whatsapp" })}
                      className="flex-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 min-w-[120px]"
                      data-testid={`btn-wa-${p.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEnviarModal({ pessoa: p })}
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      data-testid={`btn-enviar-${p.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" /> Funil
                    </Button>
                    <BlacklistPessoaBtn pessoa={p} onChanged={load} />
                  </div>
                </div>
              );
            })}
            {pessoas.length === 0 && <div className="text-slate-400 text-sm">Sem pessoas.</div>}
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h2 className="font-display font-bold text-xl">Timeline</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={timelinePessoaId}
                onChange={(e) => setTimelinePessoaId(e.target.value)}
                className="text-xs h-8 rounded border border-slate-200 bg-white px-2"
                data-testid="timeline-pessoa-filter"
              >
                <option value="">Todas as pessoas</option>
                {pessoas.map((p) => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </select>
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">
                {showAllTimeline ? "Histórico completo" : "Últimos 60 dias"}
              </span>
              <button
                onClick={() => setShowAllTimeline((v) => !v)}
                className="text-[10px] text-blue-600 hover:underline font-semibold"
                data-testid="btn-toggle-timeline"
              >
                {showAllTimeline ? "Mostrar só 60d" : "Ver tudo"}
              </button>
            </div>
          </div>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {(() => {
              const cortoIso = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
              let base = showAllTimeline ? timeline : timeline.filter((e) => e.data >= cortoIso);
              if (timelinePessoaId) {
                base = base.filter((e) => e.payload?.pessoa_id === timelinePessoaId);
              }
              const eventosVisiveis = base;
              const eventosAntigos = showAllTimeline ? 0 : (timelinePessoaId ? 0 : timeline.length - eventosVisiveis.length);
              return (
                <>
                  {eventosVisiveis.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm border-l-2 border-slate-200 pl-3 py-1" data-testid={`tl-item-${i}`}>
                      <div className="flex-shrink-0 mt-0.5">
                        {e.tipo === "contato" && (e.payload.canal === "ligacao"
                          ? <Phone className="w-4 h-4 text-blue-500" />
                          : <MessageCircle className="w-4 h-4 text-emerald-500" />)}
                        {e.tipo === "inscricao" && <Trophy className="w-4 h-4 text-amber-500" />}
                        {e.tipo === "perda" && <TrendingDown className="w-4 h-4 text-red-500" />}
                        {e.tipo === "card_move" && <ArrowRight className="w-4 h-4 text-slate-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        {e.tipo === "contato" && (
                          <div>
                            <span className="text-slate-500">
                              {e.payload.canal === "ligacao" ? "📞 Ligação para " : "💬 WhatsApp para "}
                            </span>
                            <b className="text-slate-900">{e.payload.pessoa_nome || "—"}</b>
                            {e.payload.pessoa_funcao && (
                              <span className="text-xs text-slate-400"> ({e.payload.pessoa_funcao})</span>
                            )}
                            {e.payload.observacao && (
                              <div className="text-slate-600 italic mt-0.5">"{e.payload.observacao}"</div>
                            )}
                          </div>
                        )}
                        {e.tipo === "inscricao" && (
                          <>
                            <div className="font-semibold">Inscrição: {e.payload.quantidade} pessoas · {formatBRL(e.payload.valor_vendido)}</div>
                            <button
                              onClick={() => setEnviarConfInscricaoId(e.payload.inscricao_id || e.ref_id)}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#1d7495] hover:bg-[#0f5673] rounded-full px-2.5 py-1"
                              data-testid={`btn-enviar-conf-${e.ref_id || e.payload.inscricao_id}`}
                            >
                              <Mail className="w-3 h-3" /> ENVIAR CONFIRMAÇÃO DE INSCRIÇÃO
                            </button>
                          </>
                        )}
                        {e.tipo === "perda" && <div>Perda: <b>{e.payload.categoria}</b> — {e.payload.sub}</div>}
                        {e.tipo === "card_move" && <div>Movido: {ESTAGIO_LABELS[e.payload.de]} → {ESTAGIO_LABELS[e.payload.para]}</div>}
                        <div className="text-xs text-slate-400">{formatDateTime(e.data)}</div>
                      </div>
                    </div>
                  ))}
                  {eventosVisiveis.length === 0 && <div className="text-slate-400 text-sm">Sem eventos nos últimos 60 dias.</div>}
                  {eventosAntigos > 0 && (
                    <button
                      onClick={() => setShowAllTimeline(true)}
                      className="w-full mt-2 py-2 text-xs text-slate-500 hover:text-slate-700 border border-dashed border-slate-200 rounded-lg hover:border-slate-300"
                      data-testid="btn-ver-historico-completo"
                    >
                      +{eventosAntigos} evento{eventosAntigos > 1 ? "s" : ""} mais antigo{eventosAntigos > 1 ? "s" : ""} — clique para ver histórico completo
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </Card>
      </div>

      {/* Editar */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Editar município / órgão</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{["Câmara", "Prefeitura"].includes(form.tipo_orgao || "Câmara") ? "Município *" : "Nome completo do órgão *"}</Label><Input value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="edit-nome" /></div>
                <div>
                  <Label>Órgão</Label>
                  <Select value={form.tipo_orgao || "Câmara"} onValueChange={(v) => setForm({ ...form, tipo_orgao: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_ORGAO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>UF</Label><Input value={form.uf || ""} onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} maxLength={2} /></div>
                <div><Label>CNPJ</Label><Input value={form.cnpj || ""} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></div>
                <div><Label>Telefone geral</Label><Input value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
                <div><Label>E-mail institucional</Label><Input type="email" value={form.email_institucional || ""} onChange={(e) => setForm({ ...form, email_institucional: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>E-mails adicionais (envio de confirmações e propostas)</Label>
                  <EmailsChipsInput
                    emails={form.emails || []}
                    onChange={(list) => setForm({ ...form, emails: list })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Digite um e-mail e pressione Enter ou vírgula. Todos serão adicionados como destinatários automaticamente.</p>
                </div>
                <div><Label>Site</Label><Input value={form.site || ""} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="www.exemplo.gov.br" /></div>
                <div><Label>Qtd. vereadores</Label><Input type="number" value={form.qtd_vereadores || ""} onChange={(e) => setForm({ ...form, qtd_vereadores: Number(e.target.value) })} /></div>
                <div className="col-span-2"><Label>Endereço</Label><Input value={form.endereco || ""} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></div>
                <div className="col-span-2"><Label>Perfil político predominante</Label><Input value={form.perfil_politico || ""} onChange={(e) => setForm({ ...form, perfil_politico: e.target.value })} placeholder="Ex.: Centro-direita, base do prefeito X" /></div>
                <div className="col-span-2"><Label>Observações institucionais</Label><Textarea rows={3} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={salvarEdit} data-testid="edit-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blacklist */}
      <Dialog open={blOpen} onOpenChange={setBlOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar à Blacklist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Marque este município como perdido para a concorrência. Ele aparecerá na sua Inteligência Comercial → Blacklist para recuperação.</div>
            <div>
              <Label>Motivo</Label>
              <Textarea rows={3} value={blMotivo} onChange={(e) => setBlMotivo(e.target.value)} placeholder="Ex.: Fechou com concorrente X, orçamento comprometido..." data-testid="bl-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlOpen(false)}>Cancelar</Button>
            <Button onClick={toggleBlacklist} className="bg-red-600 hover:bg-red-700" data-testid="bl-confirm">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal registrar contato */}
      <ContatoRegistrarModal
        open={!!contatoModal}
        onClose={() => setContatoModal(null)}
        pessoa={contatoModal?.pessoa}
        canal={contatoModal?.canal}
        municipioId={id}
        onSaved={() => { setContatoModal(null); load(); }}
      />

      {/* Modal enviar pessoa para funil */}
      {enviarModal && (
        <EnviarPessoaFunilModal
          open={!!enviarModal}
          onClose={() => setEnviarModal(null)}
          pessoa={enviarModal.pessoa}
          municipioId={id}
          municipioUf={mun?.uf}
          onDone={() => { setEnviarModal(null); load(); }}
        />
      )}

      {/* Modal Registrar Inscrição */}
      <InscricaoModal
        open={!!inscricaoCard}
        onClose={() => setInscricaoCard(null)}
        onSave={salvarInscricao}
        card={inscricaoCard}
        mun={mun}
      />

      {/* Modal Enviar Confirmação de Inscrição (Iter 34) */}
      {enviarConfInscricaoId && (() => {
        const ins = inscricoes.find((i) => (i.ref_id === enviarConfInscricaoId) || (i.payload?.inscricao_id === enviarConfInscricaoId));
        const payload = ins?.payload || {};
        return (
          <ConfirmacaoEmailModal
            open={true}
            onClose={() => setEnviarConfInscricaoId(null)}
            onConfirm={async (emailOpts) => {
              // Iter 61 · "Salvar sem enviar e-mail" → não chama o backend
              // (a inscrição já existe; o botão só serve pra fechar a modal sem spam).
              if (emailOpts?.enviar_email === false) {
                toast.success("✉️ Inscrição mantida sem envio de e-mail.");
                setEnviarConfInscricaoId(null);
                return;
              }
              try {
                await api.post(`/inscricoes/${enviarConfInscricaoId}/enviar-confirmacao`, emailOpts);
                toast.success("✉️ Confirmação de inscrição enviada!");
                setEnviarConfInscricaoId(null);
                load();
              } catch (e) {
                // Iter 61/62 · Toast persistente (15s) + ação "Já enviei por fora" quando o Resend bloqueia
                const msg = apiErrorMsg(e, "Falha ao enviar");
                const isBlocked = msg.includes("não entregável") || msg.includes("bloqueou") || msg.includes("manualmente");
                toast.error(msg, {
                  duration: isBlocked ? 15000 : 6000,
                  description: isBlocked ? "Abra o seu Gmail/Outlook e envie o e-mail direto da sua conta — depois clique em \"Já enviei\" na inscrição pra ficar registrado." : undefined,
                  action: isBlocked ? {
                    label: "Já enviei",
                    onClick: () => setEnvioManualIns({ id: enviarConfInscricaoId, destinatariosSugeridos: emailOpts?.email_destinatarios || [] }),
                  } : undefined,
                });
              }
            }}
            previewPayload={{
              card_id: payload.card_id,
              quantidade: payload.quantidade || 1,
              valor_vendido: payload.valor_vendido || 0,
              valor_cheio: payload.valor_cheio || 0,
              alunos_pessoa_ids: payload.alunos_pessoa_ids || [],
            }}
          />
        );
      })()}

      {/* Iter 62: Modal Registrar envio manual */}
      <RegistrarEnvioManualModal
        open={!!envioManualIns}
        inscricaoId={envioManualIns?.id}
        destinatariosSugeridos={envioManualIns?.destinatariosSugeridos || []}
        onClose={() => setEnvioManualIns(null)}
        onDone={() => { setEnvioManualIns(null); load(); }}
      />

      {/* Modal Perfil da Pessoa (timeline filtrada por 30d / mês) */}
      <PessoaPerfilModal
        open={!!pessoaPerfilId}
        pessoaId={pessoaPerfilId}
        municipioNome={mun?.nome}
        onClose={() => setPessoaPerfilId(null)}
        onRegistrarContato={(pessoa, canal) => {
          setPessoaPerfilId(null);
          setContatoModal({ pessoa, canal });
        }}
        onExcluido={load}
      />

      {/* Iter 50: Indicar município para o time CeL Consultoria */}
      <IndicarCelModal
        open={indicarCelOpen}
        onClose={() => setIndicarCelOpen(false)}
        municipioInicial={mun ? { nome: mun.nome, uf: mun.uf, cnpj: mun.cnpj, tipo_orgao: mun.tipo_orgao } : null}
      />

      {/* Iter 48: Modal Lixeira - Pessoas deletadas (só diretoria) */}
      <Dialog open={lixeiraOpen} onOpenChange={setLixeiraOpen}>
        <DialogContent className="max-w-2xl" data-testid="modal-lixeira-pessoas">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-slate-600" /> Lixeira · {mun?.nome}
            </DialogTitle>
            <DialogDescription>
              Pessoas excluídas deste município. Somente a Diretoria vê. Restaurar volta a pessoa para a lista da vendedora.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {lixeiraLoading ? (
              <div className="space-y-2 py-2" data-testid="lixeira-loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3 animate-pulse space-y-2">
                    <div className="h-4 w-2/5 bg-slate-200 rounded" />
                    <div className="h-3 w-3/5 bg-slate-100 rounded" />
                    <div className="h-2 w-4/5 bg-slate-100 rounded" />
                  </div>
                ))}
              </div>
            ) : pessoasDeletadas.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-8" data-testid="lixeira-vazia">
                Nenhuma pessoa excluída neste município.
              </div>
            ) : pessoasDeletadas.map((p) => (
              <div key={p.id} className="flex items-start justify-between gap-3 border border-slate-200 rounded-lg p-3 hover:bg-slate-50" data-testid={`lixeira-item-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900">{p.nome}</div>
                  <div className="text-xs text-slate-600">{p.funcao || "Sem cargo"}{p.email ? ` · ${p.email}` : ""}</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Excluída em {p.deletado_em ? new Date(p.deletado_em).toLocaleString("pt-BR") : "?"}
                    {p.deletado_por_nome ? ` por ${p.deletado_por_nome}` : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={lixeiraBusy}
                  onClick={async () => {
                    setLixeiraBusy(true);
                    try {
                      await api.post(`/pessoas/${p.id}/restaurar`);
                      toast.success(`${p.nome} restaurada`);
                      setPessoasDeletadas((v) => v.filter((x) => x.id !== p.id));
                      load();
                    } catch (e) {
                      toast.error(apiErrorMsg(e, "Erro ao restaurar"));
                    } finally { setLixeiraBusy(false); }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid={`btn-restaurar-${p.id}`}
                >
                  ↩ Restaurar
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLixeiraOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nova pessoa */}
      <Dialog open={novaPessoaOpen} onOpenChange={setNovaPessoaOpen}>        <DialogContent data-testid="modal-nova-pessoa">
          <DialogHeader><DialogTitle>Nova pessoa em {mun?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={novaPessoa.nome}
                onChange={(e) => setNovaPessoa({ ...novaPessoa, nome: e.target.value })}
                data-testid="nova-pessoa-nome"
                autoFocus
              />
            </div>
            <div>
              <Label>Nome de urna / Apelido</Label>
              <Input
                value={novaPessoa.nome_urna}
                onChange={(e) => setNovaPessoa({ ...novaPessoa, nome_urna: e.target.value })}
                placeholder="Ex: João do Bairro"
                data-testid="nova-pessoa-nome-urna"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Função</Label>
                <Input
                  value={novaPessoa.funcao}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, funcao: e.target.value })}
                  placeholder="Vereador, Presidente, Contador..."
                  data-testid="nova-pessoa-funcao"
                />
              </div>
              <div>
                <Label>Partido</Label>
                <Input
                  value={novaPessoa.partido}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, partido: e.target.value })}
                  placeholder="PT, PP, PSD..."
                  data-testid="nova-pessoa-partido"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={novaPessoa.telefone}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, telefone: e.target.value })}
                  placeholder="(48) 99999-8888"
                  data-testid="nova-pessoa-telefone"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={novaPessoa.whatsapp}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, whatsapp: e.target.value })}
                  placeholder="(48) 99999-8888"
                  data-testid="nova-pessoa-whatsapp"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={novaPessoa.email}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, email: e.target.value })}
                  data-testid="nova-pessoa-email"
                />
              </div>
              <div>
                <Label>Nascimento (AAAA-MM-DD)</Label>
                <Input
                  value={novaPessoa.data_nascimento}
                  onChange={(e) => setNovaPessoa({ ...novaPessoa, data_nascimento: e.target.value })}
                  placeholder="1980-05-15"
                  data-testid="nova-pessoa-nasc"
                />
              </div>
            </div>
            <div>
              <Label>Instagram</Label>
              <Input
                value={novaPessoa.instagram}
                onChange={(e) => setNovaPessoa({ ...novaPessoa, instagram: e.target.value })}
                placeholder="@usuario"
                data-testid="nova-pessoa-instagram"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaPessoaOpen(false)}>Cancelar</Button>
            <Button onClick={criarPessoa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-salvar-nova-pessoa">
              <Plus className="w-4 h-4 mr-1" /> Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Botão inteligente que abre "Registrar Inscrição" ou "Proposta":
   - 0 cards ativos → botão desabilitado (com tooltip)
   - 1 card → aciona direto
   - 2+ cards → abre um mini-diálogo com a lista de cursos para o usuário escolher
*/
function AcaoCurso({ cardsAtivos, variant, onPick }) {
  const [open, setOpen] = useState(false);
  const disabled = !cardsAtivos || cardsAtivos.length === 0;

  const label = variant === "inscricao" ? "Registrar Inscrição" : "Proposta";
  const cls = variant === "inscricao"
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : "border border-blue-300 text-blue-700 hover:bg-blue-50 bg-white";
  const testid = variant === "inscricao" ? "btn-registrar-inscricao" : "btn-emitir-proposta";
  const Ico = variant === "inscricao" ? Target : FileText;
  const emoji = variant === "inscricao" ? "🎯" : "📄";

  const handleClick = () => {
    if (disabled) return;
    if (cardsAtivos.length === 1) {
      onPick(cardsAtivos[0]);
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled}
        className={cls}
        data-testid={testid}
        title={disabled ? "Envie este município para um funil antes" : ""}
      >
        <Ico className="w-4 h-4 mr-1" /> {emoji} {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-picker-curso">
          <DialogHeader>
            <DialogTitle>{variant === "inscricao" ? "Em qual curso registrar a inscrição?" : "Proposta para qual curso?"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {cardsAtivos.map((ca) => (
              <button
                key={ca.id}
                onClick={() => { onPick(ca); setOpen(false); }}
                className="w-full text-left rounded-xl border border-slate-200 p-3 hover:border-emerald-400 hover:shadow-sm transition"
                data-testid={`picker-card-${ca.id}`}
              >
                <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                  {ca.curso_uf && <span className="text-xs font-bold text-slate-400">{ca.curso_uf}</span>}
                  <span>{ca.curso_nome}</span>
                  <span className="text-xs text-slate-400 font-normal">· {ca.curso_mes_ano}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${ESTAGIO_CHIP[ca.estagio]}`}>
                    {ESTAGIO_LABELS[ca.estagio]}
                  </span>
                  {ca.vendedora_nome && <span>· {ca.vendedora_nome}</span>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Modal para enviar UMA pessoa direto para curso+estágio */
const UFS_BR = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI",
  "RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];
const MESES_BR = [
  { v: "01", l: "Jan" }, { v: "02", l: "Fev" }, { v: "03", l: "Mar" }, { v: "04", l: "Abr" },
  { v: "05", l: "Mai" }, { v: "06", l: "Jun" }, { v: "07", l: "Jul" }, { v: "08", l: "Ago" },
  { v: "09", l: "Set" }, { v: "10", l: "Out" }, { v: "11", l: "Nov" }, { v: "12", l: "Dez" },
];

function EnviarPessoaFunilModal({ open, onClose, pessoa, municipioId, municipioUf, onDone }) {
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [estagio, setEstagio] = useState("incerto");
  // Iter 57: filtros UF/Mês (fluxo obrigatório antes de escolher curso)
  const [filtroUf, setFiltroUf] = useState("");
  const [filtroAno, setFiltroAno] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  // Iter 42: motivo de perda quando escolhe "não quer"
  const [motivos, setMotivos] = useState({});
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivoCat, setMotivoCat] = useState("");
  const [motivoSub, setMotivoSub] = useState("");
  const [motivoObs, setMotivoObs] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/cursos?status=ativo").then((r) => setCursos(r.data || [])).catch(() => {});
    api.get("/motivos-perda").then((r) => setMotivos(r.data || {})).catch(() => {});
    setMotivoCat(""); setMotivoSub(""); setMotivoObs("");
    // Default UF = município da pessoa; Mês/Ano = mês atual
    setFiltroUf((municipioUf || "").toUpperCase());
    const now = new Date();
    setFiltroAno(String(now.getFullYear()));
    setFiltroMes(String(now.getMonth() + 1).padStart(2, "0"));
    setCursoId("");
  }, [open, municipioUf]);

  const anosDisponiveis = useMemo(() => {
    const set = new Set();
    (cursos || []).forEach((c) => c.mes_ano && set.add((c.mes_ano || "").split("-")[0]));
    const arr = [...set].sort().reverse();
    return arr.length ? arr : [String(new Date().getFullYear())];
  }, [cursos]);

  const cursosFiltrados = useMemo(() => {
    return (cursos || []).filter((c) => {
      // Cursos sem UF são "nacionais" — passam por qualquer filtro de UF
      if (filtroUf && c.uf && (c.uf || "").toUpperCase() !== filtroUf) return false;
      if (filtroAno && !((c.mes_ano || "").startsWith(filtroAno))) return false;
      if (filtroMes) {
        const parts = (c.mes_ano || "").split("-");
        if (parts[1] !== filtroMes) return false;
      }
      return true;
    }).sort((a, b) => (b.mes_ano || "").localeCompare(a.mes_ano || ""));
  }, [cursos, filtroUf, filtroAno, filtroMes]);

  // Se o curso selecionado deixa de estar nos filtros, limpa a seleção
  useEffect(() => {
    if (cursoId && !cursosFiltrados.some((c) => c.id === cursoId)) {
      setCursoId("");
    }
  }, [cursosFiltrados, cursoId]);

  const limparFiltros = () => { setFiltroUf(""); setFiltroAno(""); setFiltroMes(""); };

  const doPost = async (motivoPayload) => {
    try {
      const body = {
        municipio_id: municipioId,
        curso_id: cursoId,
        atribuicoes: [{ pessoa_id: pessoa.id, estagio }],
      };
      if (motivoPayload) body.motivo_perda = motivoPayload;
      await api.post("/prospeccao/enviar-curso", body);
      toast.success(`${pessoa.nome} enviada(o) para o funil`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro")); }
  };

  const enviar = async () => {
    if (!cursoId) { toast.error("Escolha um curso"); return; }
    // Iter 42: se estágio é "não quer", pergunta motivo antes
    if (estagio === "nao_querem") {
      setMotivoOpen(true);
      return;
    }
    await doPost(null);
  };

  const confirmarMotivoEEnviar = async () => {
    if (!motivoCat || !motivoSub) { toast.error("Selecione categoria e motivo"); return; }
    setMotivoOpen(false);
    await doPost({ categoria: motivoCat, sub: motivoSub, obs: motivoObs });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="modal-enviar-pessoa">
        <DialogHeader>
          <DialogTitle>Enviar {pessoa?.nome} para o funil</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Iter 57: filtros UF + Ano + Mês obrigatórios antes de selecionar o curso */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Filtrar cursos por</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Select value={filtroUf} onValueChange={(v) => setFiltroUf(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="enviar-pessoa-filtro-uf"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__all__">Todos</SelectItem>
                  {UFS_BR.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filtroAno} onValueChange={(v) => setFiltroAno(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="enviar-pessoa-filtro-ano"><SelectValue placeholder="Ano" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {anosDisponiveis.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filtroMes} onValueChange={(v) => setFiltroMes(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="enviar-pessoa-filtro-mes"><SelectValue placeholder="Mês" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {MESES_BR.map((m) => (<SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {(filtroUf || filtroAno || filtroMes) && (
              <button
                type="button"
                onClick={limparFiltros}
                className="mt-1 text-[11px] text-slate-500 hover:text-slate-800 font-bold"
                data-testid="enviar-pessoa-filtros-limpar"
              >Limpar filtros</button>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">
              Curso {cursosFiltrados.length > 0 && (<span className="text-slate-400 font-normal normal-case tracking-normal">({cursosFiltrados.length} disponíve{cursosFiltrados.length === 1 ? "l" : "is"})</span>)}
            </Label>
            <Select value={cursoId} onValueChange={setCursoId}>
              <SelectTrigger data-testid="select-curso-pessoa"><SelectValue placeholder="Escolha um curso..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cursosFiltrados.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400">Nenhum curso bate com esses filtros.</div>
                ) : cursosFiltrados.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.uf ? `${c.uf} · ` : "🇧🇷 · "}{c.nome} · {c.mes_ano}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Estágio inicial</Label>
            <Select value={estagio} onValueChange={setEstagio}>
              <SelectTrigger data-testid="select-estagio-pessoa"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="incerto">Incerto</SelectItem>
                <SelectItem value="interessado">Interessado</SelectItem>
                <SelectItem value="certo">Certo</SelectItem>
                <SelectItem value="nao_querem">Não quer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-enviar-pessoa-conf">
            <Send className="w-4 h-4 mr-1" /> Enviar
          </Button>
        </DialogFooter>

        {/* Iter 42: modal secundária de motivo de perda quando "não quer" */}
        <Dialog open={motivoOpen} onOpenChange={(o) => !o && setMotivoOpen(false)}>
          <DialogContent data-testid="motivo-perda-pessoa-dialog">
            <DialogHeader><DialogTitle>Por que {pessoa?.nome} não vai comprar?</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest">Categoria *</Label>
                <Select value={motivoCat} onValueChange={(v) => { setMotivoCat(v); setMotivoSub(""); }}>
                  <SelectTrigger data-testid="motivo-pessoa-cat"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(motivos).map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {motivoCat && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Motivo específico *</Label>
                  <Select value={motivoSub} onValueChange={setMotivoSub}>
                    <SelectTrigger data-testid="motivo-pessoa-sub"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {(motivos[motivoCat] || []).map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest">Observação (opcional)</Label>
                <Input value={motivoObs} onChange={(e) => setMotivoObs(e.target.value)} data-testid="motivo-pessoa-obs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMotivoOpen(false)}>Cancelar</Button>
              <Button onClick={confirmarMotivoEEnviar} className="bg-red-600 hover:bg-red-700" data-testid="motivo-pessoa-salvar">
                Registrar e Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
